import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomerEditForm from "../../../../../components/dashboardcomponents/People/Customers/CustomerEdit/CustomerEditForm";
import { buildCustomerEditForm } from "../../../../../components/dashboardcomponents/People/Customers/CustomerEdit/customerEditHelpers";
import {
  CUSTOMER_EDIT_CANCEL,
  CUSTOMER_EDIT_SAVE,
} from "../../../../../components/dashboardcomponents/People/Customers/customerEvents";
import { CustomerEditPageSkeleton } from "../../../../../components/dashboardcomponents/People/Customers/CustomerLoadingSkeletons";
import {
  pageSubtitleSx,
  pageTitleSx,
  TEAM_FORM_WIDTH,
} from "../../../../../components/dashboardcomponents/People/Teams/teamFormStyles";
import useCustomerFormOptions from "../../../../../hooks/useCustomerFormOptions";
import usePeoplePermissions from "../../../../../hooks/usePeoplePermissions";
import { buildCustomerUpdatePayload } from "../../../../../utils/customerMappers";
import {
  fetchCustomerById,
  updateCustomer,
} from "../../../../../store/features/customersApi";
import {
  firstValidationError,
  hasValidationErrors,
  validateCustomerForm,
} from "../../../../../components/dashboardcomponents/People/peopleFormValidation";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function CustomerEditPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading } = useSelector((state) => state.peopleCustomers);
  const { groupOptions, loading: optionsLoading } = useCustomerFormOptions({
    includeTeams: false,
  });
  const perms = usePeoplePermissions();

  const detailId = detail?.id || detail?._id;
  const initialForm = useMemo(
    () => (detailId === customerId ? buildCustomerEditForm(detail) : null),
    [detail, detailId, customerId]
  );
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customerId) dispatch(fetchCustomerById({ customerId }));
  }, [customerId, dispatch]);

  useEffect(() => {
    if (initialForm) setForm(initialForm);
  }, [initialForm]);

  const saveCustomer = useCallback(async () => {
    if (!form || saving) return;
    const errors = validateCustomerForm(form);
    if (hasValidationErrors(errors)) {
      toast.error(firstValidationError(errors));
      return;
    }

    setSaving(true);
    const statusEditable =
      perms.customers.canChangeStatus &&
      (form.status === "active" || form.status === "inactive");
    const body = buildCustomerUpdatePayload(form, { includeStatus: statusEditable });
    const result = await dispatch(updateCustomer({ customerId, body }));
    setSaving(false);

    if (updateCustomer.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Customer updated"));
      navigate(`/people/customers/${customerId}`);
    } else {
      toast.error(apiMessage(result.payload, "Failed to update customer"));
    }
  }, [form, saving, dispatch, customerId, navigate, perms]);

  const canEditStatus =
    perms.customers.canChangeStatus &&
    (form?.status === "active" || form?.status === "inactive");

  useEffect(() => {
    const onSave = () => saveCustomer();
    const onCancel = () => navigate(`/people/customers/${customerId}`);
    window.addEventListener(CUSTOMER_EDIT_SAVE, onSave);
    window.addEventListener(CUSTOMER_EDIT_CANCEL, onCancel);
    return () => {
      window.removeEventListener(CUSTOMER_EDIT_SAVE, onSave);
      window.removeEventListener(CUSTOMER_EDIT_CANCEL, onCancel);
    };
  }, [saveCustomer, navigate, customerId]);

  if ((detailLoading && !detail) || (detail && !form)) {
    return <CustomerEditPageSkeleton />;
  }

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Customer not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: 3,
        py: 3,
        bgcolor: "#FFF",
      }}
    >
      <Box sx={{ maxWidth: TEAM_FORM_WIDTH, width: "100%", mx: "auto" }}>
        <Typography sx={pageTitleSx}>Edit Customer</Typography>
        <Typography sx={pageSubtitleSx}>
          Update customer information profile tags and conversation details.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <CustomerEditForm
            form={form}
            onChange={setForm}
            groupOptions={groupOptions}
            optionsLoading={optionsLoading}
            canEditStatus={canEditStatus}
          />
        </Box>
      </Box>
    </Box>
  );
}
